import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import SnTree from "@shared/components/GridV2/SnTree";
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import SnButton from "@shared/components/SnButton";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const SN_PMS_BAS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });

  const treeRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const isNewNodeRef = useRef<boolean>(true);
  const lastSelectedNodeRef = useRef<any>(null);
  const baseNodeCount = useRef<number>(0); // 노드 개수
  const parentNodeKey = useRef<String>("");

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = treeRefs.current["tree"].current.getChange();
    const modifiedRowsD = getGridCurrent("grid3").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_pms_bas_reg_003_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      treeRefs.current["tree"].current.resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      getGridCurrent("grid1").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      baseNodeCount.current = 0;

      isNewNodeRef.current = true;
      lastSelectedNodeRef.current =
        treeRefs.current["tree"].current.getSelectedNodes();

      const selectedNode =
        lastSelectedNodeRef.current && lastSelectedNodeRef.current.length > 0
          ? lastSelectedNodeRef.current[0]
          : null;
      const parentNode = selectedNode !== null ? selectedNode.parent : null;

      // 최상위노드면 최상위 노드 갯수 카운팅 아니면 선택 노드 카운팅
      if (selectedNode) {
        if (selectedNode.data.cd_plevel === null) {
          treeRefs.current["tree"].current.forEachFilteredNode(
            (node: RowNode) => {
              if (node.data.cd_plevel === null) {
                baseNodeCount.current++;
              }
            }
          );
        } else {
          treeRefs.current["tree"].current.forEachFilteredNode(
            (node: RowNode) => {
              if (node.data.cd_plevel === selectedNode.data.cd_plevel) {
                baseNodeCount.current++;
              }
            }
          );
        }
      }

      treeRefs.current["tree"].current.addRow({}, parentNode);
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_template: focusedRowNodeH.data?.cd_template,
    });

    getGridCurrent("grid3").deleteAllRows();
    treeRefs.current["tree"].current.deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      treeRefs.current["tree"].current.resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (treeRefs.current["tree"].current?.getRowCount() < 1) {
      return;
    }

    getGridCurrent("grid3").deleteAllRows();
    treeRefs.current["tree"].current?.deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      treeRefs.current["tree"].current.stopEditing();
      getGridCurrent("grid3").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        treeRefs.current["tree"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      // D 필수값 체크
      const requiredValuesCheckD =
        getGridCurrent("grid3").requiredValuesCheck();
      if (!requiredValuesCheckD) {
        return false;
      }

      const wbshChanges = getGridCurrent("grid1").getChange();
      const wbslChanges = treeRefs.current["tree"].current.getChange();
      const wbsl_documentChanges = getGridCurrent("grid3").getChange();

      if (
        wbshChanges.modifiedRowcount === 0 &&
        wbslChanges.modifiedRowcount === 0 &&
        wbsl_documentChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 요청 데이터 구성
      const SaveData = {
        wbsh: wbshChanges,
        wbsl: wbslChanges,
        wbsl_document: wbsl_documentChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PMS_BAS_REG_003_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      treeRefs.current["tree"].current.resetUpdate();
      getGridCurrent("grid3").resetUpdate();

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      treeRefs.current["tree"].current.resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        fg_pjt: "top1",
        yn_use: "Y",
        cd_template: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  //#region TreeL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const selectedNode =
        lastSelectedNodeRef.current && lastSelectedNodeRef.current.length > 0
          ? lastSelectedNodeRef.current[0]
          : null;

      const maxNoLine = treeRefs.current["tree"].current.getMaxValue("no_line");

      const updatedRow: RowData = {
        cd_firm: userInfo.cd_firm,
        cd_dept: userInfo.cd_dept,
        cd_template: focusedRowNodeH.data.cd_template,
        no_line: maxNoLine + 1,
        yn_end: "Y",
        rt_weight: 0,
        dy_plan: 0,
        no_seq: 1,
        cd_plevel: null,
      };

      if (!selectedNode) {
        // 최상위 노드

        baseNodeCount.current = baseNodeCount.current + 1;

        updatedRow.no_level = baseNodeCount.current;
        updatedRow.cd_level =
          updatedRow.cd_template + String(updatedRow.no_level).padStart(3, "0");
      } else {
        if (isNewNodeRef.current) {
          if (selectedNode.data.cd_plevel !== null) {
            // 하위노드의 신규 추가
            baseNodeCount.current++;

            updatedRow.cd_plevel = selectedNode.data.cd_plevel;

            const result = selectedNode.data.no_level
              .split(".")
              .map((val: any, i: any, arr: any) =>
                i === arr.length - 1 ? baseNodeCount.current : val
              )
              .join(".");

            updatedRow.no_level = result.toString();
          } else {
            // 최상위 신규노드 추가
            let maxSeq = 1;
            treeRefs.current["tree"].current.forEachFilteredNode(
              (node: RowNode) => {
                if (node.data.cd_plevel === null) {
                  maxSeq++;
                }
              }
            );

            const maxNoLevel =
              treeRefs.current["tree"].current.getMaxValue("no_level");
            updatedRow.no_level = (
              Number(maxNoLevel.toString().split(".")[0]) + 1
            ).toString();
            updatedRow.no_seq = maxSeq;
          }
        } else {
          // 하위노드 추가
          baseNodeCount.current++;

          const selectedNodeChildren = selectedNode.childrenAfterGroup || [];
          const maxSeq =
            selectedNodeChildren.length > 0
              ? Math.max(
                  ...selectedNodeChildren.map(
                    (child: any) => child.data.no_seq || 0
                  )
                )
              : 0;

          updatedRow.cd_plevel = parentNodeKey.current;
          updatedRow.no_level =
            selectedNode.data.no_level + "." + baseNodeCount.current.toString();
          updatedRow.no_seq = maxSeq + 1;
        }

        updatedRow.cd_level =
          updatedRow.cd_template +
          updatedRow.no_level
            .split(".")
            .map((v: any) => v.padStart(3, "0"))
            .join("") +
          String(updatedRow.no_seq).padStart(3, "0");
      }
      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      getGridCurrent("grid3").applyFilters({
        cd_template: selectedRow.cd_template,
        no_line_wbs: selectedRow.no_line,
      });
    } else {
      getGridCurrent("grid3").applyFilters({
        cd_template: "",
        no_line_wbs: 0,
      });
    }
  };

  const onTreeLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };

  const autoGroupColumnDef = {
    headerName: "Level",
    field: "no_level",
    sort: "asc",
    width: 200,
    cellRendererParams: {
      suppressCount: true, // 하위 아이템 개수 숨기기
      suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
      suppressHoverHighlight: false,
    },
    rowDrag: true,
  };

  const getRowId = useCallback(
    (params: any) => String(params.data.cd_level),
    []
  );

  const onTreeBtnsClick = async (id: string) => {
    switch (id) {
      case "Expand":
        treeRefs.current["tree"].current.setExpandedAll(true);
        break;
      case "Coalesce":
        treeRefs.current["tree"].current.setExpandedAll(false);
        break;
      case "Add":
        isNewNodeRef.current = false;
        baseNodeCount.current = 0;

        lastSelectedNodeRef.current =
          treeRefs.current["tree"].current.getSelectedNodes();
        const selectedNode =
          lastSelectedNodeRef.current && lastSelectedNodeRef.current.length > 0
            ? lastSelectedNodeRef.current[0]
            : null;

        if (!selectedNode) {
          await showDialog("alter", "상위 ACTIVITY를 선택해주세요.");
          return;
        }

        if (selectedNode.childrenAfterGroup.length > 0) {
          await showDialog("alter", "이미 하위레벨이 추가되어있습니다.");
          return;
        }

        // 닫혀 있으면 열기
        if (!selectedNode.expanded) {
          selectedNode.setExpanded(true);
          treeRefs.current["tree"].current.onGroupExpandedOrCollapsed();
        }

        parentNodeKey.current = selectedNode.data.cd_level;
        treeRefs.current["tree"].current.addRow({}, selectedNode);
        break;
      case "Up":
        moveRow("up");
        break;
      case "Down":
        moveRow("down");
        break;
    }
  };

  const moveRow = (direction: "up" | "down") => {
    const selectedNodes = treeRefs.current["tree"].current.getSelectedNodes();

    if (selectedNodes.length === 0) return;

    const currentNode = selectedNodes[0];
    const allNodes: any[] = [];

    // 정렬/필터링된 상태의 노드 순서를 그대로 가져옵니다.
    treeRefs.current["tree"].current.forEachFilteredNode((node: RowNode) => {
      if (currentNode.data.cd_plevel === node.data.cd_plevel) {
        allNodes.push(node);
      }
    });

    // 1. 현재 노드의 위치 찾기
    const currentIndex = allNodes.findIndex(
      (node) => node.id === currentNode.id
    );
    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;

    // 2. 범위를 벗어나면 리턴 (로그 추가해서 확인)
    if (targetIndex < 0 || targetIndex >= allNodes.length) {
      return;
    }
    const targetNode = allNodes[targetIndex];

    // 3. 값 교체 로직
    const currentSeq = currentNode.data.no_seq;
    const targetSeq = targetNode.data.no_seq;
    const currentLevel = currentNode.data.no_level;
    const targetLevel = targetNode.data.no_level;

    // 4. setDataValue로 그리드에 반영
    treeRefs.current["tree"].current.forEachFilteredNode((node: RowNode) => {
      if (currentNode.data.cd_level === node.data.cd_plevel) {
        const levelParts = String(node.data.no_level).split(".");
        const lastIndex = levelParts[levelParts.length - 1];
        node.setDataValue(
          "no_level",
          targetNode.data.no_level + "." + lastIndex,
          "noEvent"
        );
      }
    });

    treeRefs.current["tree"].current.forEachFilteredNode((node: RowNode) => {
      if (targetNode.data.cd_level === node.data.cd_plevel) {
        const levelParts = String(node.data.no_level).split(".");
        const lastIndex = levelParts[levelParts.length - 1];
        node.setDataValue(
          "no_level",
          currentNode.data.no_level + "." + lastIndex,
          "noEvent"
        );
        console.log(node.data.no_level);
      }
    });

    currentNode.setDataValue("no_seq", targetSeq, "noEvent");
    targetNode.setDataValue("no_seq", currentSeq, "noEvent");
    currentNode.setDataValue("no_level", targetLevel, "noEvent");
    targetNode.setDataValue("no_level", currentLevel, "noEvent");
  };
  //#endregion

  //#region GridD Events
  const onBeforeAddRowD = (): any => {
    try {
      const focusedRowNodeL = treeRefs.current["tree"].current.focusedRowNode();
      if (focusedRowNodeL == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const maxNoLine = getGridCurrent("grid3").getMaxValue("no_line");

      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_template: focusedRowNodeL.data?.cd_template,
        no_line_wbs: focusedRowNodeL.data?.no_line,
        no_line: maxNoLine + 1,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        getGridCurrent("grid3").addRow();
        break;
      case "deleteRow":
        if (getGridCurrent("grid3").getDisplayedRowCount() < 1) {
          return;
        }

        getGridCurrent("grid3").deleteSelectedRows();
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
        },
      },
      tree: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onBtnAddClick: onTreeLBtnsClick,
          onBtnDeleteClick: onTreeLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBeforeAddRow: onBeforeAddRowD,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="50%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "템플릿정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [treeRefs.current["tree"]],
              columns: ["cd_template"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="50%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="75%">
              <SnTree
                ref={treeRefs.current["tree"]} // Grid Name
                columnConfig={grids.tree.columns} // 컬럼 설정 전달
                rowData={grids.tree.dataSource} // Datasource
                etcProps={{
                  rowSelection: "single",
                  title: "ACTIVITY",
                }}
                useTreeTempKey={false}
                events={grids.tree.events}
                treeProps={{
                  treeDataParentIdField: "cd_plevel",
                  treeIdKey: "cd_level",
                  treeOrderKey: "no_seq",
                  groupDefaultExpanded: -1,
                  autoGroupColumnDef: autoGroupColumnDef,
                  getRowId: getRowId,
                }}
                header={
                  <div
                    style={{
                      paddingRight: "80px",
                      display: "flex",
                    }}
                  >
                    <div style={{ paddingRight: "80px" }}>
                      <SnButton
                        text="전체전개"
                        onClick={() => onTreeBtnsClick("Expand")}
                      />
                      <SnButton
                        text="전체닫기"
                        onClick={() => onTreeBtnsClick("Coalesce")}
                      />
                    </div>
                    <div>
                      <SnButton
                        text="하위레벨추가"
                        onClick={() => onTreeBtnsClick("Add")}
                      />
                      <SnButton
                        text="▲"
                        onClick={() => onTreeBtnsClick("Up")}
                      />
                      <SnButton
                        text="▼"
                        onClick={() => onTreeBtnsClick("Down")}
                      />
                    </div>
                  </div>
                }
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0">
              <SnGrid
                ref={gridRefs.current["grid3"]} // Grid Name
                columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                rowData={grids.grid3.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "산출물정보" }}
                events={grids.grid3.events}
              />
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PMS_BAS_REG_003;
